import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { css } from '@emotion/core';
import i18n from 'i18next';

import { prizeTypes, getPrizeTypeLabel } from 'config/promoPrizeTypes';
import { updateWinnerAnimationFlag } from 'api/client';
import { setShouldUpdateUserWinnerList, setAnimatingLotteries } from 'client/redux/modules/flags';
import {
    clearCurrentPrizeUUID,
    clearCurrentUnanimationEntriesUUID,
    setGameNotReadyToPlay
} from 'client/redux/modules/purePrizeState';
import { gtmPushPrize } from 'client/utils/tracking';
import parseAnimationSettings from 'utils/parseAnimationSettings';

import { usePurePrizeGamePrizeList } from 'client/hooks/useLotteryPrizeList';
import useClientHistory, { RESULT, PRIZE } from 'client/hooks/useClientHistory';

import PopupWinnerGet from 'client/components/Prize/PopupWinnerGet';
import useGetLotteryAndPrizeAndPromoData from 'hooks/useGetLotteryAndPrizeAndPromoData';
import useClientErrorHandler from 'client/hooks/useClientErrorHandler';

export default function PurePrizeGameContainer({ onPurePrizeGameFinish, children }) {
    const dispatch = useDispatch();
    const history = useClientHistory();
    const errHandler = useClientErrorHandler();
    const [isWinnerModalOpen, setIsWinnerModalOpen] = useState(false);
    const { loginId } = useSelector(s => s.userLoginState);
    const {
        currentEntryUUID,
        hasGameResult,
        hasFilledSignupForm,
        hasParticipantPrize
    } = useSelector(s => s.userEntryState);
    const { currentPrizeUUID, isGameReadyToPlay, currentUnanimationEntriesUUID } = useSelector(
        s => s.purePrizeState
    );
    const { AnimationSettings, type: lotteryParticipantType, uuid: lotteryParticipantUUID } =
        useSelector(s => s.lotteryParticipant) || {};
    const parsedAnimationSettings = useMemo(() => parseAnimationSettings(AnimationSettings ?? {}), [
        AnimationSettings
    ]);
    const entriesUUID = currentUnanimationEntriesUUID || currentEntryUUID;
    const lotteryParticipantPrizeList = usePurePrizeGamePrizeList();

    const { getPromoByPrizeAndEntry } = useGetLotteryAndPrizeAndPromoData();
    const { uuid: promoUUID, id: promoId } = getPromoByPrizeAndEntry(currentPrizeUUID, entriesUUID);

    const onWinnerModalOpenHandler = () => setIsWinnerModalOpen(true);
    const onWinnerModalCloseHandler = () => setIsWinnerModalOpen(false);

    const callbackAfterAnimationPlayed = useCallback(() => {
        dispatch(setGameNotReadyToPlay());
        onWinnerModalOpenHandler();
    }, [dispatch]);

    // 執行純獎流程的 callback
    const executePurePrizeFlowHandler = useCallback(async () => {
        // Ex: 點擊戳戳樂外框，已經開始執行純獎流程，但戳戳樂沒有戳破獎項，所以沒有拿到 prizeUUID，所以提早 return，不執行 store game result
        if (currentPrizeUUID) {
            return;
        }
        // 選定要配的獎項，但還沒 store game result，執行 onPurePrizeGameFinish
        if (!hasGameResult || !hasFilledSignupForm || !hasParticipantPrize) {
            await onPurePrizeGameFinish();
        }
    }, [
        currentPrizeUUID,
        hasFilledSignupForm,
        hasGameResult,
        hasParticipantPrize,
        onPurePrizeGameFinish
    ]);

    // 遊戲設定及是否可遊玩 flag
    const gameProps = useMemo(() => {
        return {
            // 已經 parse 過的動畫設定值，若拿到空物件則代表沒有任何動畫設定值，這時候需要給遊戲本身的預設值
            animationSettings: parsedAnimationSettings,

            // isGameReadyToPlay 代表純獎流程的前置作業都跑好且條件也都滿足了
            // 遊戲隨時可以開始的意思，可以把它作為遊戲動畫的依賴觸發
            isGameReadyToPlay
        };
    }, [parsedAnimationSettings, isGameReadyToPlay]);

    // 獎品相關資訊
    const prizeProps = useMemo(() => {
        const winnerInfo =
            // 當抓到參加獎的獎品資訊以及得獎獎品 UUID 時才搜尋，否則給空物件，避免回傳 undefined
            lotteryParticipantPrizeList && currentPrizeUUID
                ? lotteryParticipantPrizeList.find(
                      lotteryParticipantPrize => lotteryParticipantPrize.uuid === currentPrizeUUID
                  ) || {}
                : {};
        // 取得得獎獎品相關資訊，獎品圖網址、獎品名稱、獎品類型
        const { settings: { imageUrl } = {}, name: prizeText, type: prizeType } = winnerInfo;
        return {
            // 參加獎的獎品列表
            lotteryParticipantPrizeList,
            // 獲獎的獎品相關資訊
            winnerInfo: {
                prizeUUID: currentPrizeUUID,
                prizeImageUrl: imageUrl,
                prizeText,
                prizeType
            }
        };
    }, [currentPrizeUUID, lotteryParticipantPrizeList]);

    // 開獎與發送 prize gtm event
    const updateWinnerFlag = useCallback(async () => {
        try {
            if (currentPrizeUUID) {
                await updateWinnerAnimationFlag(promoUUID, entriesUUID, currentPrizeUUID);
                gtmPushPrize({
                    prizeName: prizeProps.winnerInfo.prizeText,
                    prizeType: getPrizeTypeLabel(prizeProps.winnerInfo.prizeType),
                    loginId,
                    promoId
                });
                dispatch(setShouldUpdateUserWinnerList());
            }
        } catch (error) {
            console.log('Failed to update winner animation flag:', error);
            errHandler({ err: error });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPrizeUUID, entriesUUID, promoUUID]);

    // 各種更新狀態的 callback
    const callbackProps = useMemo(
        () => ({
            // 更新看過獎品動畫的 flag
            updateWinnerFlag,
            // 在動畫結束後要呼叫的 callback，它會去 update 純獎的動畫狀態，並且開啟得獎 pop-up
            callbackAfterAnimationPlayed,
            executePurePrizeFlowHandler
        }),
        [callbackAfterAnimationPlayed, executePurePrizeFlowHandler, updateWinnerFlag]
    );

    const isThanksPrize = prizeProps.winnerInfo.prizeType === prizeTypes.THANKYOU;

    useEffect(() => {
        if (lotteryParticipantUUID) {
            dispatch(
                setAnimatingLotteries({
                    type: lotteryParticipantType,
                    uuid: lotteryParticipantUUID,
                    animationSettings: AnimationSettings
                })
            );
        }
    }, [AnimationSettings, dispatch, lotteryParticipantType, lotteryParticipantUUID]);

    return (
        <div css={rootContainerStyle}>
            {lotteryParticipantPrizeList && (
                <>
                    {children({
                        ...gameProps,
                        ...prizeProps,
                        ...callbackProps,
                        onPurePrizeGameFinish
                    })}
                    <PopupWinnerGet
                        title={
                            isThanksPrize
                                ? i18n.t('label.keep_up_the_work')
                                : i18n.t('label.congratulation')
                        }
                        label={prizeProps.winnerInfo.prizeText}
                        imgSrc={prizeProps.winnerInfo.prizeImageUrl}
                        isOpen={isWinnerModalOpen}
                        onConfirm={() => {
                            onWinnerModalCloseHandler();
                            dispatch(clearCurrentPrizeUUID());
                            dispatch(clearCurrentUnanimationEntriesUUID());

                            if (isThanksPrize) {
                                history.replace(RESULT);
                            } else {
                                history.replace(PRIZE, {
                                    prizeUUID: currentPrizeUUID,
                                    entriesUUID: entriesUUID
                                });
                            }
                        }}
                        btnText={
                            isThanksPrize ? i18n.t('btn.close') : i18n.t('button.receive_prize')
                        }
                    />
                </>
            )}
        </div>
    );
}

const rootContainerStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
`;
